import * as React from 'react'

import type { ToastActionElement, ToastProps } from '@/components/common/ui/toast'
import { crossTabCommunication, type NotificationData } from '@/utils/cross-tab-communication'

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 1000000

// トースト優先度の定義
export type ToastPriority = 'critical' | 'high' | 'medium' | 'low'

// トーストカテゴリの定義
export type ToastCategory = 'batch' | 'system' | 'user' | 'general'

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  // 🔧 トーストのメタデータを追加
  priority?: ToastPriority
  category?: ToastCategory
}

// Define action types directly as a type instead of a const object
type ActionType = {
  ADD_TOAST: 'ADD_TOAST'
  UPDATE_TOAST: 'UPDATE_TOAST'
  DISMISS_TOAST: 'DISMISS_TOAST'
  REMOVE_TOAST: 'REMOVE_TOAST'
}

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_VALUE
  return count.toString()
}

// Use ActionType in the Action type definition
type Action =
  | {
      type: ActionType['ADD_TOAST']
      toast: ToasterToast
    }
  | {
      type: ActionType['UPDATE_TOAST']
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType['DISMISS_TOAST']
      toastId?: ToasterToast['id']
    }
  | {
      type: ActionType['REMOVE_TOAST']
      toastId?: ToasterToast['id']
    }

interface State {
  toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map(t => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      }

    case 'DISMISS_TOAST': {
      const { toastId } = action

      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach(toast => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map(t =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter(t => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach(listener => {
    listener(memoryState)
  })
}

// Add a property to make the interface distinct from its parent type
interface Toast extends Omit<ToasterToast, 'id'> {
  customData?: unknown
  // 🔧 新しいメタデータプロパティを公開
  priority?: ToastPriority
  category?: ToastCategory
  // Cross-tab notification support
  crossTab?: boolean
  source?: 'local' | 'cross-tab'
}

function toast({ ...props }: Toast) {
  const id = genId()

  const update = (props: Partial<ToasterToast>) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...(props as ToasterToast), id },
    })
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id })

  // Handle cross-tab broadcasting
  if (props.crossTab && props.source !== 'cross-tab') {
    const notificationData: NotificationData = {
      title: props.title?.toString() || '',
      description: props.description?.toString(),
      variant: props.variant === 'info' ? 'default' : (props.variant ?? undefined),
      duration: props.duration,
    }
    crossTabCommunication.broadcastToastNotification(notificationData)
  }

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: open => {
        if (!open) dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  // Subscribe to cross-tab notifications
  React.useEffect(() => {
    const unsubscribe = crossTabCommunication.subscribeToToastNotifications(
      (notificationData: NotificationData) => {
        // Show notification received from another tab with enhanced visual indicators
        toast({
          title: `🔗 ${notificationData.title}`,
          description: notificationData.description
            ? `${notificationData.description}\n\n📺 別タブからの通知`
            : '📺 別タブからの通知',
          variant: notificationData.variant ?? 'default',
          duration: notificationData.duration || 8000, // Longer duration for cross-tab notifications
          source: 'cross-tab',
          className: 'border-l-4 border-l-blue-500 bg-blue-50/90 shadow-lg shadow-blue-100/50', // Enhanced visual indicators
        })
      }
    )

    return unsubscribe
  }, [])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  }
}

export { useToast, toast }
